import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import WorkflowProgress from '../../components/ui/WorkflowProgress';
import PlaylistPreview from './components/PlaylistPreview';
import TrackMatchingPreview from './components/TrackMatchingPreview';
import SyncStatistics from './components/SyncStatistics';
import AdvancedOptions from './components/AdvancedOptions';
import SyncActionButtons from './components/SyncActionButtons';
import Icon from '../../components/AppIcon';
import { matchTracks } from '../../utils/trackMatching';


const SyncPreviewConfiguration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Mock playlist data
  const [sourcePlaylist] = useState({
    id: 'spotify_playlist_1',
    name: 'My Favorite Hits 2024',
    description: 'A collection of my favorite songs from 2024, featuring the best pop, rock, and indie tracks that defined the year.',
    coverImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop',
    trackCount: 45,
    totalDuration: 10800, // 3 hours
    owner: 'john.doe',
    isPublic: true,
    platform: 'spotify'
  });

  const [destinationPlatform] = useState({
    id: 'youtube_music',
    name: 'YouTube Music',
    description: 'Sync to YouTube Music platform',
    coverImage: 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=400&h=400&fit=crop',
    trackCount: 0,
    totalDuration: 0,
    owner: 'john.doe',
    isPublic: false,
    platform: 'youtube'
  });

  // Mock source and destination track data
  const sourceTracks = [
    { id: 1, title: 'Blinding Lights', artist: 'The Weeknd', album: 'After Hours', duration: 200 },
    { id: 2, title: 'Watermelon Sugar', artist: 'Harry Styles', album: 'Fine Line', duration: 174 },
    { id: 3, title: 'Levitating', artist: 'Dua Lipa', album: 'Future Nostalgia', duration: 203 },
    { id: 4, title: 'Good 4 U', artist: 'Olivia Rodrigo', album: 'SOUR', duration: 178 },
    { id: 5, title: 'Heat Waves', artist: 'Glass Animals', album: 'Dreamland', duration: 238 },
    { id: 6, title: 'Stay', artist: 'The Kid LAROI & Justin Bieber', album: 'F*CK LOVE 3: OVER YOU', duration: 141 },
    { id: 7, title: 'Industry Baby', artist: 'Lil Nas X & Jack Harlow', album: 'MONTERO', duration: 212 },
    { id: 8, title: 'Peaches', artist: 'Justin Bieber ft. Daniel Caesar & Giveon', album: 'Justice', duration: 198 }
  ];
  const destinationTracks = [
    { id: 'yt1', title: 'Blinding Lights', artist: 'The Weeknd', album: 'After Hours', duration: 200 },
    { id: 'yt2', title: 'Watermelon Sugar', artist: 'Harry Styles', album: 'Fine Line', duration: 174 },
    { id: 'yt3', title: 'Levitating', artist: 'Dua Lipa', album: 'Future Nostalgia', duration: 203 },
    { id: 'yt4', title: 'good 4 u', artist: 'Olivia Rodrigo', album: 'SOUR', duration: 178 },
    { id: 'yt5', title: 'Heat Waves', artist: 'Glass Animals', album: 'Dreamland', duration: 238 },
    { id: 'yt6', title: 'STAY', artist: 'The Kid LAROI, Justin Bieber', album: 'F*CK LOVE 3: OVER YOU', duration: 141 },
    { id: 'yt7', title: 'INDUSTRY BABY', artist: 'Lil Nas X ft. Jack Harlow', album: 'MONTERO', duration: 212 },
    { id: 'yt8', title: 'Peaches', artist: 'Justin Bieber', album: 'Justice', duration: 198 }
  ];

  // Generate track matches
  const [tracks, setTracks] = useState([]);
  useEffect(() => {
    const matched = matchTracks(sourceTracks, destinationTracks).map(track => ({ ...track, selected: true }));
    setTracks(matched);
  }, []);

  // Advanced options state
  const [advancedOptions, setAdvancedOptions] = useState({
    duplicateHandling: 'skip',
    matchSensitivity: 'moderate',
    preserveDescription: true,
    preserveOrder: true,
    createBackup: false,
    manualReview: false
  });

  console.log('tracks', tracks)

  // Calculate statistics
  const stats = {
    totalTracks: tracks.length,
    exactMatches: tracks.filter(t => t.matchConfidence >= 0.9).length,
    similarMatches: tracks.filter(t => t.matchConfidence >= 0.7 && t.matchConfidence < 0.9).length,
    noMatches: tracks.filter(t => t.matchConfidence < 0.7).length,
    selectedTracks: tracks.filter(t => t.selected).length
  };

  const handleTrackToggle = (trackId) => {
    setTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId
          ? { ...track, selected: !track.selected }
          : track
      )
    );
  };

  const handleStartSync = async () => {
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      navigate('/sync-progress-real-time-tracking');
    }, 2000);
  };

  const canStartSync = stats.selectedTracks > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* <Header /> */}
      <WorkflowProgress />
      
      <main className="pt-32 lg:pt-36 pb-8">
        <div className="px-4 lg:px-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-muted mb-2">
              <span>Sync Setup</span>
              <Icon name="ChevronRight" size={16} />
              <span className="text-text-primary">Preview & Configuration</span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
              Review Your Sync
            </h1>
            <p className="text-text-secondary">
              Review the playlist details and track matching results before starting the sync process.
            </p>
          </div>

          <div className="space-y-6">
            {/* Playlist Preview Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PlaylistPreview 
                playlist={sourcePlaylist} 
                type="source"
              />
              <PlaylistPreview 
                playlist={destinationPlatform} 
                type="destination"
              />
            </div>

            {/* Statistics Overview */}
            <SyncStatistics stats={stats} />

            {/* Track Matching Preview */}
            <TrackMatchingPreview 
              tracks={tracks}
              onTrackToggle={handleTrackToggle}
            />

            {/* Advanced Options */}
            <AdvancedOptions 
              options={advancedOptions}
              onOptionsChange={setAdvancedOptions}
            />

            {/* Action Buttons */}
            <SyncActionButtons 
              onStartSync={handleStartSync}
              canStartSync={canStartSync}
              isLoading={isLoading}
              stats={stats}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default SyncPreviewConfiguration;